import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  type Document,
} from "../api/documents";

export function DocumentsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["documents"], queryFn: listDocuments });
  const [editing, setEditing] = useState<Document | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => deleteDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading documents…</p>;
  const rows = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Documents</h1>
        <button
          onClick={() => setEditing("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New document
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">File</th>
              <th className="px-4 py-2">Length</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((d) => (
              <tr key={d.document_id}>
                <td className="px-4 py-2 font-medium">{d.name}</td>
                <td className="px-4 py-2 text-gray-600">{d.type || "—"}</td>
                <td className="px-4 py-2 font-mono text-gray-600">{d.file_name || "—"}</td>
                <td className="px-4 py-2">{d.length}</td>
                <td className="px-4 py-2 text-right space-x-3">
                  <button onClick={() => setEditing(d)} className="text-brand hover:underline">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete document "${d.name}"?`)) remove.mutate(d.document_id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-gray-400 text-sm p-4">No documents defined.</p>}
      </div>

      {editing && (
        <Editor
          document={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["documents"] });
          }}
        />
      )}
    </div>
  );
}

function Editor({
  document,
  onClose,
  onSaved,
}: {
  document: Document | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(document?.name ?? "");
  const [type, setType] = useState(document?.type ?? "");
  const [quality, setQuality] = useState(document?.quality ?? "standard");
  const [fileName, setFileName] = useState(document?.file_name ?? "");
  const [description, setDescription] = useState(document?.description ?? "");
  const [length, setLength] = useState(document?.length ?? 0);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        type,
        quality,
        file_name: fileName,
        description,
        length,
      };
      return document
        ? updateDocument(document.document_id, body)
        : createDocument(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
        <h2 className="text-lg font-bold">{document ? "Edit" : "New"} document</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Quality</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">File name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Length</label>
          <input
            type="number"
            min={0}
            className="w-32 border border-gray-300 rounded px-3 py-2 text-sm"
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
